import { useTranslation } from "react-i18next"

import { KudoCard } from "@/components/crew/kudo-card"
import type { RecentKudo } from "@/types/crew"

/**
 * OKS-396 — RECENT KUDOS strip on Brand/Crew/Show (UX Spec §2.B).
 *
 * Renders up to 3 `KudoCard`s newest-first (BE-capped + BE-ordered — the
 * FE never re-sorts or re-slices), or a friendly empty state inviting the
 * first kudo. Ships inside the page's existing payload, so there is no
 * separate spinner/loading state here.
 *
 * 1–2 kudos render only those cards (no ghost placeholders); the empty
 * state shows ONLY when the list is truly empty.
 *
 * Locked testids:
 *   - `recent-kudos-section` — section wrapper
 *   - `recent-kudos-empty`   — empty-state line (only when no kudos)
 *   - `kudo-card`            — per-kudo (owned by `<KudoCard>`)
 */
interface RecentKudosSectionProps {
  recentKudos: RecentKudo[]
  /** Creator display name — interpolated into the empty-state copy. */
  creatorName: string
}

export function RecentKudosSection({
  recentKudos,
  creatorName,
}: RecentKudosSectionProps) {
  const { t } = useTranslation()
  const isEmpty = recentKudos.length === 0

  return (
    <section data-testid="recent-kudos-section" className="flex flex-col gap-3">
      <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        {t("crew.detail.kudos.recent_heading")}
      </h3>
      {isEmpty ? (
        <p
          data-testid="recent-kudos-empty"
          className="text-muted-foreground bg-muted/30 rounded-md border p-3 text-sm"
        >
          {t("crew.detail.kudos.empty", { name: creatorName })}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {recentKudos.map((kudo) => (
            <KudoCard key={kudo.id} kudo={kudo} />
          ))}
        </div>
      )}
    </section>
  )
}
